import { useAjax } from '../hooks/useAjax';

/**
 * API Endpoints
 * 
 * Wrappers for all 12 AJAX actions.
 * Used by components to call PHP endpoints.
 */

export function useApi() {
  const { request } = useAjax();

  return {
    // Officer endpoints
    getChangeRequestForm: (categoryId, statDate) =>
      request('dst_get_change_request_form', { category_id: categoryId, stat_date: statDate }),

    submitChangeRequest: (categoryId, statDate, newValue, reason, valueField = 'stat_value') =>
      request('dst_submit_change_request', {
        category_id: categoryId,
        stat_date: statDate,
        new_value: newValue,
        reason,
        value_field: valueField,
      }),

    getMyStatsForDate: (statDate) =>
      request('dst_get_my_stats_for_date', { stat_date: statDate }, { method: 'GET' }),

    saveStatDraft: (statDate, draft) =>
      request('dst_save_stat_draft', { stat_date: statDate, draft: JSON.stringify(draft) }),

    submitDailyStats: (statDate, stats) =>
      request('dst_submit_daily_stats', {
        stat_date: statDate,
        stats: JSON.stringify(stats),
      }),

    getMyStats: (dateFrom, dateTo) =>
      request('dst_get_my_stats', { date_from: dateFrom, date_to: dateTo }, { method: 'GET' }),

    getPatrolRequests: () =>
      request('dst_get_officer_patrol_requests'),

    patrolCheckIn: (requestId, note = '') =>
      request('dst_patrol_check_in', { request_id: requestId, note }),

    getPatrolDetail: (requestId) =>
      request('dst_patrol_request_detail', { request_id: requestId }),

    recentPatrolChecks: () =>
      request('dst_patrols_recent_checks_json'),

    getDepartmentStats: ({ shiftId = '', categoryId = '', dateFrom = '', dateTo = '', statDate = '' } = {}) =>
      request('dst_get_department_stats_table', {
        shift_id: shiftId,
        category_id: categoryId,
        date_from: dateFrom,
        date_to: dateTo,
        stat_date: statDate,
      }, { method: 'POST' }),

    // Admin endpoints
    saveShiftOrder: (order) =>
      request('lep_save_shift_order', { order: JSON.stringify(order) }),

    saveCategoryOrder: (order) =>
      request('lep_save_category_order', { order: JSON.stringify(order) }),

    saveRankOrder: (order) =>
      request('lep_save_rank_order', { order: JSON.stringify(order) }),

    updatePatrolRequest: (requestId, type, address, city, notes, priority = 'normal') =>
      request('dst_patrol_admin_update_request', { request_id: requestId, type_id: type, address, city, notes, priority }),

    createPatrolRequest: ({ type_id, address, city, notes, priority = 'normal' }) =>
      request('dst_patrol_admin_new_request', { type_id, address, city, notes, priority }),

    deletePatrolRequest: (requestId) =>
      request('dst_patrol_admin_delete_request', { request_id: requestId }),

    archivePatrolRequest: (requestId) =>
      request('dst_patrol_admin_archive_request', { request_id: requestId, operation: 'archive' }),

    restorePatrolRequest: (requestId) =>
      request('dst_patrol_admin_archive_request', { request_id: requestId, operation: 'restore' }),

    togglePatrolActive: (requestId, status) =>
      request('dst_patrol_admin_toggle_active', { request_id: requestId, status }),

    getAdminPatrolRequests: () =>
      request('dst_patrol_admin_recent_requests'),

    getAdminPatrolTypes: () =>
      request('dst_patrol_admin_types_list', {}, { method: 'GET' }),

    addPatrolType: (name) =>
      request('dst_patrol_add_type', { name }),

    togglePatrolType: (id, active) =>
      request('dst_patrol_toggle_type', { id, active }),

    deletePatrolType: (id) =>
      request('dst_patrol_delete_type', { id }),

    exportPatrolRequestsCsv: ({ from = '', to = '', type_id = '' } = {}) =>
      request('dst_patrol_export_csv', { from, to, type_id }, { method: 'GET' }),

    exportPatrolChecksCsv: ({ from = '', to = '' } = {}) =>
      request('lep_patrol_checks_export_csv', { from, to }, { method: 'GET' }),

    getAdminChangeRequests: (filters = {}) =>
      request('dst_admin_get_change_requests', filters, { method: 'GET' }),

    moderateChangeRequest: (requestId, moderationAction, adminNote = '') =>
      request('dst_admin_change_request_action', {
        request_id: requestId,
        moderation_action: moderationAction,
        admin_note: adminNote,
      }),

    getViewStatsTable: (filters = {}) =>
      request('dst_view_stats', filters, { method: 'GET' }),

    // Admin CRUD endpoints
    saveGeneralSettings: (settings) =>
      request('lep_save_general_settings', settings),

    savePatrolAreas: (areas) =>
      request('lep_save_patrol_areas', { areas: JSON.stringify(areas) }),

    addOfficer: (officer) =>
      request('lep_add_officer', officer),

    editOfficer: (id, officer) =>
      request('lep_edit_officer', { id, ...officer }),

    deleteOfficer: (id) =>
      request('lep_delete_officer', { id }),

    addCategory: (category) =>
      request('lep_add_category', category),

    editCategory: (id, category) =>
      request('lep_edit_category', { id, ...category }),

    deleteCategory: (id) =>
      request('lep_delete_category', { id }),

    addShift: (shift) =>
      request('lep_add_shift', shift),

    editShift: (id, shift) =>
      request('lep_edit_shift', { id, ...shift }),

    deleteShift: (id) =>
      request('lep_delete_shift', { id }),

    saveAppearance: (colors) =>
      request('lep_save_appearance', colors),

    // API Views management
    listApiViews: () =>
      request('lep_api_views_list', {}, { method: 'GET' }),

    addApiView: (data) =>
      request('lep_api_view_add', data),

    editApiView: (viewId, data) =>
      request('lep_api_view_edit', { view_id: viewId, ...data }),

    deleteApiView: (viewId) =>
      request('lep_api_view_delete', { view_id: viewId }),

    toggleApiView: (viewId, isEnabled) =>
      request('lep_api_view_toggle', { view_id: viewId, is_enabled: isEnabled ? 1 : 0 }),

    regenApiViewKey: (viewId) =>
      request('lep_api_view_regen_key', { view_id: viewId }),

    recreateApiViewPage: (viewId) =>
      request('lep_api_view_recreate_page', { view_id: viewId }),

    getLogs: ({ action_filter = '', date_from = '', date_to = '' } = {}) =>
      request('lep_get_logs', { action_filter, date_from, date_to }, { method: 'GET' }),
  };
}
